import { useState, useEffect } from "react";
import {
  User,
  Building2,
  FileText,
  Upload,
  Link2,
  MessageSquare,
  Sparkles,
  Check,
  ChevronDown,
  ChevronUp,
  Mic,
  AlertCircle,
  X,
  Loader2,
  Edit3
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get proper image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  if (path.startsWith('/uploads') || path.startsWith('/api')) {
    return `${process.env.REACT_APP_BACKEND_URL}${path}`;
  }
  return path;
};

// Helper to format link URLs properly
const formatLinkUrl = (url) => {
  if (!url) return '#';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `https://${url}`;
};

export default function SpeakerIntakeCard({ 
  rsvpId, 
  eventId, 
  initialData = {}, 
  onComplete,
  isExpanded = false,
  readOnly = false
}) {
  const [expanded, setExpanded] = useState(isExpanded);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [completed, setCompleted] = useState(initialData?.speaker_intake_completed || false);
  
  const [formData, setFormData] = useState({
    name: initialData?.name || "",
    pronunciation: initialData?.pronunciation || "",
    title: initialData?.title || "",
    company: initialData?.company || "",
    bio: initialData?.bio || "",
    headshot_url: initialData?.headshot_url || "",
    logo_url: initialData?.logo_url || "",
    link_url: initialData?.link_url || "",
    link_label: initialData?.link_label || "",
    talking_points: initialData?.talking_points || "",
    topics_to_avoid: initialData?.topics_to_avoid || "",
    promotions: initialData?.promotions || "",
    ...initialData?.speaker_intake
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImageUpload = async (field, file) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      formDataUpload.append("type", field === "headshot_url" ? "headshot" : "logo");
      formDataUpload.append("rsvp_id", rsvpId || "");
      
      // Use speaker-specific upload endpoint (no auth required)
      const res = await fetch(`${API}/speaker/upload-image`, {
        method: "POST",
        body: formDataUpload
      });
      
      if (res.ok) {
        const data = await res.json();
        handleInputChange(field, data.url);
        toast.success(`${field === "headshot_url" ? "Headshot" : "Logo"} uploaded!`);
      } else {
        toast.error("Upload failed");
      }
    } catch (err) {
      toast.error("Upload error");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/events/${eventId}/rsvp/${rsvpId}/speaker-intake`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          speaker_intake: formData,
          speaker_intake_completed: true
        })
      });
      
      if (res.ok) {
        setCompleted(true);
        setExpanded(false);  // Close the form after save to show edit view
        toast.success("Speaker info saved!");
        onComplete?.(formData);
      } else {
        toast.error("Failed to save");
      }
    } catch (err) {
      toast.error("Error saving");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    setExpanded(false);
  };

  // If completed and read-only mode, show summary
  if (completed && readOnly) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <CardTitle className="text-base">Speaker Info Complete</CardTitle>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </Button>
          </div>
        </CardHeader>
        
        {expanded && (
          <CardContent className="pt-0 space-y-4">
            {/* Summary display */}
            <div className="grid grid-cols-2 gap-4">
              {formData.headshot_url && (
                <div className="col-span-2 flex justify-center">
                  <img 
                    src={getImageUrl(formData.headshot_url)} 
                    alt="Headshot" 
                    className="w-24 h-24 rounded-full object-cover border-2 border-white shadow-md"
                  />
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="font-medium">{formData.name}</p>
                {formData.pronunciation && (
                  <p className="text-sm text-gray-500 italic">({formData.pronunciation})</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500">Title & Company</p>
                <p className="font-medium">{formData.title}</p>
                <p className="text-sm text-gray-500">{formData.company}</p>
              </div>
            </div>
            
            {formData.bio && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Bio</p>
                <p className="text-sm">{formData.bio}</p>
              </div>
            )}
            
            {formData.talking_points && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Talking Points / Expertise</p>
                <p className="text-sm">{formData.talking_points}</p>
              </div>
            )}
            
            {formData.link_url && (
              <div>
                <p className="text-xs text-gray-500 mb-1">Link</p>
                <a href={formatLinkUrl(formData.link_url)} target="_blank" rel="noopener noreferrer" className="text-sm text-purple-600 hover:underline">
                  {formData.link_label || formData.link_url}
                </a>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    );
  }

  // If completed but NOT read-only (user can edit), show editable summary
  if (completed && !readOnly && !expanded) {
    return (
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                <Check className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-base">Speaker Info Submitted</CardTitle>
                <p className="text-xs text-gray-500">Your information is visible to the host</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setExpanded(true)}
              className="text-purple-600 border-purple-200 hover:bg-purple-50"
            >
              <Edit3 className="w-3 h-3 mr-1" />
              Edit Info
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="pt-0 space-y-3">
          {/* Compact Summary */}
          <div className="flex items-start gap-4">
            {formData.headshot_url ? (
              <img 
                src={getImageUrl(formData.headshot_url)} 
                alt="Headshot" 
                className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-md flex-shrink-0"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                <User className="w-6 h-6 text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium">{formData.name || "No name"}</p>
              {formData.pronunciation && (
                <p className="text-xs text-gray-500 italic">({formData.pronunciation})</p>
              )}
              <p className="text-sm text-purple-600">{formData.title || "No title"}</p>
              {formData.company && (
                <p className="text-xs text-gray-500">{formData.company}</p>
              )}
            </div>
          </div>
          
          {formData.bio && (
            <div className="text-sm text-gray-600 bg-white rounded-lg p-3 border">
              <p className="text-xs font-medium text-gray-500 mb-1">Bio</p>
              <p className="line-clamp-3">{formData.bio}</p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Incomplete state - show prompt or full form
  if (!expanded) {
    return (
      <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-pink-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Mic className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Panel Speaker Info</h4>
                <p className="text-sm text-gray-500">
                  {completed ? "Your info has been submitted" : "Help us introduce you properly"}
                </p>
              </div>
            </div>
            <Button 
              onClick={() => setExpanded(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="speaker-intake-expand-btn"
            >
              {completed ? "View Info" : "Add Details"}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Expanded form view
  return (
    <Card className="border-purple-200 overflow-hidden">
      <CardHeader className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mic className="w-6 h-6" />
            <div>
              <CardTitle className="text-lg">Panel Speaker Information</CardTitle>
              <p className="text-sm text-white/80">Help us introduce you to the audience</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleSkip}
            className="text-white/80 hover:text-white hover:bg-white/10"
          >
            <X className="w-4 h-4 mr-1" />
            Skip for now
          </Button>
        </div>
      </CardHeader>
      
      <CardContent className="p-6 space-y-6">
        {/* Basic Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-400" />
              Full Name
            </Label>
            <Input
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Your full name"
              className="mt-1"
              data-testid="speaker-name-input"
            />
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-gray-400" />
              Pronunciation (optional)
            </Label>
            <Input
              value={formData.pronunciation}
              onChange={(e) => handleInputChange("pronunciation", e.target.value)}
              placeholder="How to pronounce your name"
              className="mt-1"
              data-testid="speaker-pronunciation-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-gray-400" />
              Title
            </Label>
            <Input
              value={formData.title}
              onChange={(e) => handleInputChange("title", e.target.value)}
              placeholder="e.g. CEO, Founder, Director"
              className="mt-1"
              data-testid="speaker-title-input"
            />
          </div>
          <div>
            <Label>Company / Organization</Label>
            <Input
              value={formData.company}
              onChange={(e) => handleInputChange("company", e.target.value)}
              placeholder="Your company name"
              className="mt-1"
              data-testid="speaker-company-input"
            />
          </div>
        </div>

        {/* Bio */}
        <div>
          <Label className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-400" />
            Bio
          </Label>
          <Textarea
            value={formData.bio}
            onChange={(e) => handleInputChange("bio", e.target.value)}
            placeholder="A short bio to introduce you (2-3 sentences)"
            className="mt-1 min-h-[100px]"
            data-testid="speaker-bio-input"
          />
        </div>

        {/* Headshot Upload */}
        <div>
          <Label className="flex items-center gap-2">
            <Upload className="w-4 h-4 text-gray-400" />
            Headshot
          </Label>
          <div className="mt-2 flex items-center gap-4">
            {formData.headshot_url ? (
              <img 
                src={getImageUrl(formData.headshot_url)} 
                alt="Headshot preview" 
                className="w-20 h-20 rounded-full object-cover border-2 border-gray-200"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="w-8 h-8 text-gray-300" />
              </div>
            )}
            <div className="flex-1">
              <Input
                type="file"
                accept="image/*"
                onChange={(e) => handleImageUpload("headshot_url", e.target.files[0])}
                className="text-sm"
                disabled={uploading}
                data-testid="speaker-headshot-input"
              />
              <p className="text-xs text-gray-500 mt-1">Square photo recommended (min 400x400px)</p>
            </div>
          </div>
        </div>

        {/* Logo Upload (optional) */}
        <div>
          <Label className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-gray-400" />
            Company Logo (optional)
          </Label>
          <div className="mt-2 flex items-center gap-4">
            {formData.logo_url ? (
              <img 
                src={getImageUrl(formData.logo_url)} 
                alt="Logo preview" 
                className="w-20 h-12 object-contain border rounded"
              />
            ) : (
              <div className="w-20 h-12 rounded bg-gray-100 flex items-center justify-center">
                <Building2 className="w-6 h-6 text-gray-300" />
              </div>
            )}
            <Input
              type="file"
              accept="image/*"
              onChange={(e) => handleImageUpload("logo_url", e.target.files[0])}
              className="text-sm flex-1"
              disabled={uploading}
              data-testid="speaker-logo-input"
            />
          </div>
        </div>

        {/* Link (optional) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-2">
              <Link2 className="w-4 h-4 text-gray-400" />
              Link URL (optional)
            </Label>
            <Input
              value={formData.link_url}
              onChange={(e) => handleInputChange("link_url", e.target.value)}
              placeholder="https://..."
              className="mt-1"
              data-testid="speaker-link-input"
            />
          </div>
          <div>
            <Label>Link Label</Label>
            <Input
              value={formData.link_label}
              onChange={(e) => handleInputChange("link_label", e.target.value)}
              placeholder="e.g. Website, LinkedIn"
              className="mt-1"
            />
          </div>
        </div>

        {/* Talking Points */}
        <div>
          <Label className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-gray-400" />
            Talking Points / Areas of Expertise (optional)
          </Label>
          <Textarea
            value={formData.talking_points}
            onChange={(e) => handleInputChange("talking_points", e.target.value)}
            placeholder="Key topics you'd like to discuss or points you want to get across..."
            className="mt-1 min-h-[80px]"
            data-testid="speaker-talking-points-input"
          />
        </div>

        {/* Topics to Avoid */}
        <div>
          <Label className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-gray-400" />
            Topics to Avoid (optional)
          </Label>
          <Textarea
            value={formData.topics_to_avoid}
            onChange={(e) => handleInputChange("topics_to_avoid", e.target.value)}
            placeholder="Anything you'd prefer not to discuss..."
            className="mt-1 min-h-[60px]"
            data-testid="speaker-avoid-input"
          />
          <p className="text-xs text-gray-500 mt-1">We'll make sure the moderator is aware</p>
        </div>

        {/* Promotions */}
        <div>
          <Label className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-gray-400" />
            Events / Promotions to Share (optional)
          </Label>
          <Textarea
            value={formData.promotions}
            onChange={(e) => handleInputChange("promotions", e.target.value)}
            placeholder="Any upcoming events or promotions you'd like to mention to the audience..."
            className="mt-1 min-h-[60px]"
            data-testid="speaker-promotions-input"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t">
          <Button 
            onClick={handleSave}
            disabled={saving || !formData.name}
            className="flex-1 bg-purple-600 hover:bg-purple-700"
            data-testid="speaker-intake-save-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Save Speaker Info
              </>
            )}
          </Button>
          <Button 
            variant="outline"
            onClick={handleSkip}
            disabled={saving}
          >
            Skip for Now
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
